import React, { useEffect, useState } from 'react';
import "./mdreader.css";
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// Import syntax highlighter directly from the main package
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
// Add fallback import in case of issues
// import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyToClipboard } from 'react-copy-to-clipboard';

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

const MdReader: React.FC = () => {
  const [searchParams] = useSearchParams();
  const articleId = searchParams.get('article');
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Add debug state to check if the library is loading correctly
  const [debug, setDebug] = useState<string | null>(null);

  useEffect(() => {
    // Debug check for React Syntax Highlighter
    try {
      if (!SyntaxHighlighter) {
        setDebug("SyntaxHighlighter not loaded");
      } else if (!vscDarkPlus) {
        setDebug("Style not loaded");
      } else {
        setDebug(null);
      }
    } catch (e) {
      setDebug(`Error loading dependencies: ${e}`);
    }

    if (!articleId) {
      setError("No article ID provided");
      return;
    }
    
    const fetchMarkdown = async () => {
      try {
        const res = await fetch(`/assets/articles/${articleId}.md`);
        if (!res.ok) throw new Error('Article not found');
        const text = await res.text();
        setMarkdown(text);
      } catch (err) {
        setError('Could not load article');
      }
    };
    
    fetchMarkdown();
  }, [articleId]);

  if (error) return <div className="text-red-500">{error}</div>;
  if (debug) return <div className="text-yellow-500">{debug}</div>;
  if (!markdown) return <div>Loading...</div>;

  // Test markdown with code blocks to ensure rendering works
  const testMarkdown = markdown.includes('```') ? markdown : markdown;
    // markdown + '\n\n```javascript\n// Test code block\nfunction test() {\n  console.log("Hello world");\n}\n```';

  return (
    <div className="body-wrapper">
      <div className="article-wrapper">
        <div className="prose mx-auto p-4">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code: ({ node, inline, className, children, ...props }: CodeProps) => {
                const match = /language-(\w+)/.exec(className || '');
                
                // For inline code
                if (inline) {
                  return (
                    <code className={`${className || ''} bg-gray-200 px-1 rounded`} {...props}>
                      {children}
                    </code>
                  );
                }
                
                // For code blocks with language specification
                if (match) {
                  const language = match[1];
                  const codeString = String(children).replace(/\n$/, '');
                  
                  return (
                    <div className="relative my-4 border border-gray-300 rounded-md overflow-hidden" style={{ background: "#282c34" }}>
                      <div className="flex justify-between items-center px-4 py-2 bg-gray-800 text-white">
                        <span className="text-xs font-mono">{language}</span>
                        <CopyToClipboard text={codeString}>
                          <button 
                            className="px-2 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600 text-white"
                            onClick={() => alert("Code copied!")} // Added for debugging
                          >
                            Copy
                          </button>
                        </CopyToClipboard>
                      </div>
                      
                      <div className="overflow-auto">
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={language}
                          showLineNumbers
                          customStyle={{ margin: 0, padding: '1rem' }}
                        >
                          {codeString}
                        </SyntaxHighlighter>
                      </div>
                    </div>
                  );
                }
                
                // For code blocks without language specification
                return (
                  <div className="relative my-4 border border-gray-300 rounded-md overflow-hidden bg-gray-100 p-4">
                    <code className={`${className || ''} block`} {...props}>
                      {children}
                    </code>
                  </div>
                );
              }
            }}
          >
            {testMarkdown}
          </ReactMarkdown>
        </div>
      </div>
      <div className="sidebar-wrapper">
        <span>Additional Articles</span>
      </div>
    </div>
  );
};

export default MdReader;
